import { always, cond, equals, T, pathOr } from 'ramda'
import React from 'react'
import styled, { css } from 'styled-components'
import { Highlight } from 'react-instantsearch/dom'
import { useRecJobsContext } from '../RecommendedJobsProvider'
import pgaLogo from '../../../assets/images/updatedPgaMemberLogo.png'
import Link from '../../common/components/Link/index'
import { isToLowerCasePropEqual } from '../../common/functions'
import getJobFeatures from './getJobFeatures'
import { Badge, Theme } from '@pga/pga-component-library'

const getRandom = max => Math.round(Math.random() * max)

const imgBaseUrl = 'https://s3.amazonaws.com/pga.org/employment/jobs/small/'
const getImageId = hit => hit.image_set_id || getRandom(10)
const isPgaProApplicantGroup = isToLowerCasePropEqual('pga professional only', 'applicant_group')
const isInternshipApplicantGroup = isToLowerCasePropEqual('pga golf management university internship', 'applicant_group')

export const reorderForDesktop = iconName => {
  return cond([
    [equals('building'), always(1)],
    [equals('location'), always(4)],
    [equals('dollar'), always(2)],
    [equals('coinDollar'), always(3)],
    [equals('calendar'), always(5)],
    [equals('exclamation'), always(6)],
    [equals('postjob'), always(7)],
    [T, always(0)]
  ])(iconName)
}

export const reorderForMobile = iconName => {
  return cond([
    [equals('building'), always(1)],
    [equals('location'), always(2)],
    [equals('dollar'), always(3)],
    [equals('coinDollar'), always(7)],
    [equals('calendar'), always(6)],
    [equals('exclamation'), always(5)],
    [equals('postjob'), always(4)],
    [T, always(0)]
  ])(iconName)
}

export const SearchResultItemContainer = styled.div`
  display: flex;
  width: 100%;
  min-height: 150px;
  flex-direction: row;
  margin-bottom: 32px;
  box-shadow: 0 5px 10px 0 ${props => props.theme.primaryShadow};

  @media (max-width: 767.99px) {
    flex-direction: column;
    height: 100%;
    margin-bottom: 16px;
  }
`

export const SearchResultItemImage = styled.div`
  flex: 0 0 150px;
  background-image: url(${props => props.imageUrl});
  background-repeat: no-repeat;
  background-size: auto 100%;
  background-color: ${props => props.theme.colors.lightGray};
  overflow: hidden;

  @media (max-width: 767.99px) {
    background-size: 100% auto;
    flex: 0 0 125px;
  }
`

export const SearchResultItemBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.white};
  overflow: hidden;
  text-align: left;
  padding: 20px;
`

const SearchResultTitleCss = css`
  font-style: normal;
  font-size: 20px;
  font-weight: bold;
`

export const SearchResultItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 36px;
  .ais-Highlight-highlighted {
    background: #bccef7;
    ${SearchResultTitleCss};
  }
  .ais-Highlight-nonHighlighted {
    ${SearchResultTitleCss};
  }
`

export const SearchResultItemTitle = styled.div`
  color: ${props => props.theme.colors.secondary};
  font-family: ${props => props.theme.fonts.body};
  font-weight: ${props => props.theme.fonts.weights.bold};

  height: 35px;
  font-size: 24px;
  letter-spacing: -0.19px;
  line-height: 22px;

  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  @media (max-width: 767.99px) {
    font-size: 22px;
    letter-spacing: -0.17px;
    overflow-wrap: break-word;
  }
`

export const SearchResultBodyItems = styled.div`
  display: flex;
  flex-wrap: wrap;
  font-family: ${props => props.theme.fonts.heading};
  font-weight: ${props => props.theme.fonts.weights.normal};
  color: ${props => props.theme.searchResultItem};
  font-feature-settings: 'liga', 'dlig';

  font-size: 12px;
  line-height: 18px;

  @media (min-width: 768px) {
    font-size: 12px;
  }
  @media (min-width: 1366px) {
    font-size: 14px;
  }
`

export const SearchResultBodyItem = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: center;
  background-color: ${props => props.theme.colors.white};
  color: ${props => props.theme.colors.secondary};
  padding: 5px 0;

  @media (min-width: 567px) {
    width: 50%;
  }

  order: ${props => reorderForMobile(props.iconName)};
  @media (min-width: 768px) {
    width: 32%;
    padding: 0;
    margin: 4px 8px 4px 0px;
    order: ${props => reorderForDesktop(props.iconName)};
  }

  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

const PGAIcon = styled.i.attrs(props => ({
  className: `pga-icon pga-${props.name}-icon`
}))`
  text-align: center;
  height: 36;
`

export const SearchResultBodyItemIcon = styled(PGAIcon)`
  align-items: center;
  justify-content: center;
  display: flex;
  height: 16px;
  width: 16px;
  min-width: 16px;
  min-height: 16px;
  margin-right: 12px;
  color: ${props => props.theme.colors.blueGray};
  font-size: ${props => (props.name === 'dollar' ? '10px' : '14px')};
`

const SearchResultContainer = styled(Link)`
  color: initial;
  text-decoration: initial;
  :hover {
    text-decoration: initial;
  }

`

const RecommendedJobBadge = ({ objectID }) => {
  const recJobs = useRecJobsContext()
  const recommendedJobIds = pathOr({}, ['data', 'recommendedJobsIds'], recJobs)

  return (
    recommendedJobIds[objectID] ? (
      <Badge color={Theme.colors.gold}>Recommended</Badge>
    ) : null
  )
}

export default ({ hit, hit: { objectID, jobTitle } }) => (
  <SearchResultContainer to={`/detail/${objectID}`}>
    <SearchResultItemContainer>
      <SearchResultItemImage imageUrl={`${imgBaseUrl}small-${getImageId(hit)}.jpg`} />
      <SearchResultItemBody>

        <SearchResultItemHeader>
          <SearchResultItemTitle>{isInternshipApplicantGroup(hit) && 'Internship: ' }
            <Highlight hit={hit} attribute='jobTitle' />
          </SearchResultItemTitle>

          <div className='d-flex align-items-center'>
            <RecommendedJobBadge objectID={objectID} />

            {isInternshipApplicantGroup(hit) &&
              <Badge color={Theme.colors.primary}>INTERNSHIP</Badge>}

            {isPgaProApplicantGroup(hit) &&
              <img src={pgaLogo} height='36px' alt='member professional logo' />}
          </div>

        </SearchResultItemHeader>
        <SearchResultBodyItems>
          {getJobFeatures(hit)
            .filter(({ value }) => value)
            .map(({ iconName, value }, i) => (
              <SearchResultBodyItem key={i} iconName={iconName}>
                <SearchResultBodyItemIcon name={iconName} />
                <div>{value}</div>
              </SearchResultBodyItem>
            ))}
        </SearchResultBodyItems>
      </SearchResultItemBody>
    </SearchResultItemContainer>
  </SearchResultContainer>
)
